import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { useState, useEffect, type ReactNode } from "react";
import cx from "ui/cx";
import type { WithTooltipProps } from "ui/popovers/Tooltip";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./ToggleGroup.module.scss";

export type ToggleGroupStyle = "primary" | "secondary" | "tertiary";

export interface ToggleGroupSingleProps
  extends Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Root>, "asChild" | "type"> {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  allowEmpty?: boolean;
  children: ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
  toggleStyle?: ToggleGroupStyle;
}

export interface ToggleGroupMultipleProps
  extends Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Root>, "asChild" | "type"> {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  allowEmpty?: boolean;
  children: ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
  toggleStyle?: ToggleGroupStyle;
}

export type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps;

export interface ToggleGroupItemProps
  extends Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Item>, "asChild">,
    WithTooltipProps {
  children: ReactNode;
  className?: string;
}

function ToggleGroupRoot(props: ToggleGroupProps) {
  const {
    children,
    toggleStyle = "primary",
    disabled = false,
    className,
    orientation = "horizontal",
    allowEmpty = true,
    onValueChange,
    value: propValue,
    defaultValue,
    type,
    ...restProps
  } = props;

  const getInitialValue = (): string | string[] => {
    if (type === "single") {
      return propValue ?? defaultValue ?? "";
    } else {
      return propValue ?? defaultValue ?? [];
    }
  };

  const [internalValue, setInternalValue] = useState<string | string[]>(getInitialValue);

  // Determine if this is a controlled component
  const isControlled = propValue !== undefined;

  // Sync internal state when controlled value changes
  useEffect(() => {
    if (isControlled && propValue !== undefined) {
      setInternalValue(propValue);
    }
  }, [isControlled, propValue]);

  // Use controlled value if provided, otherwise use internal state
  const currentValue = isControlled ? propValue : internalValue;

  // Create props based on the discriminated union
  const radixProps = {
    disabled,
    orientation,
    className: cx(
      styles.root,
      orientation === "vertical" && styles.vertical,
      disabled && styles.disabled,
      styles[toggleStyle],
      className,
    ),
    "data-testid": "toggle-group",
    ...restProps,
  };

  if (type === "single") {
    return (
      <ToggleGroupPrimitive.Root
        {...radixProps}
        type="single"
        value={currentValue as string}
        onValueChange={(newValue: string) => {
          if (!disabled && (newValue !== "" || allowEmpty)) {
            // Update internal state if uncontrolled
            if (!isControlled) {
              setInternalValue(newValue);
            }
            // Call the callback
            onValueChange?.(newValue);
          }
        }}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    );
  } else {
    return (
      <ToggleGroupPrimitive.Root
        {...radixProps}
        type="multiple"
        value={currentValue as string[]}
        onValueChange={(newValue: string[]) => {
          if (!disabled && (newValue.length > 0 || allowEmpty)) {
            // Update internal state if uncontrolled
            if (!isControlled) {
              setInternalValue(newValue);
            }
            // Call the callback
            onValueChange?.(newValue);
          }
        }}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    );
  }
}

function ToggleGroupItem({ children, disabled = false, className, tooltip, ...props }: ToggleGroupItemProps) {
  return (
    <ToggleGroupPrimitive.Item disabled={disabled} className={styles.item} data-testid="toggle-group-item" {...props}>
      {/* Tooltip requires an element to pass props too, so wrapping in div */}
      {wrapWithTooltip(<div className={cx(styles.itemWrapper, className)}>{children}</div>, tooltip)}
    </ToggleGroupPrimitive.Item>
  );
}

// Compound component exports
export const ToggleGroup = {
  Root: ToggleGroupRoot,
  Item: ToggleGroupItem,
};

// Individual component exports for flexibility
export { ToggleGroupRoot, ToggleGroupItem };

export default ToggleGroup;
